import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated, useSpring } from 'react-spring';
import useInterval from '@use-it/interval';
import { useSelector, useDispatch } from 'react-redux';

import LeaderboardTable from './BoardTable';
import Spinner from '../../../shared/_TBComponents/Spinner';
import SubText from '../../../shared/_TBComponents/SubText';
import { fetchLeaderboard } from '../ducks/leaderboardActions';

const LoadMoreContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 20px 15px;
  justify-content: center;
  position: relative;
`;

const BoardTableStyles = {
  overflow: 'scroll',
  flexGrow: 1,
  paddingBottom: '30px',
};

const LeaderboardTableWrapper = ({
  activeBoards,
  id,
  windowSize,
}) => {
  const tableRef = useRef();
  const tableContainerRef = useRef();
  const [tableHeight, setTableHeight] = useState();
  const [tableContainerHeight, setTableContainerHeight] = useState(0);
  const [tableScroll, setTableScroll] = useState(0);
  const [currentScroll, setCurrentScroll] = useState(0);

  const leaderboardData = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardData,
  );
  const leaderboardForm = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardForm,
  );
  const leaderboardSettings = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].settings,
  );
  const isEndOfList = useSelector((state) => state.leaderboard.data.leaderboards[id].isEndOfList);
  const isLeaderboardLoading = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].isLeaderboardLoading,
  );

  const [data, setData] = useState([]);

  useEffect(() => {
    setData(
      (activeBoards !== 1 || windowSize.width <= 768)
        ? leaderboardData.slice(1)
        : leaderboardData.slice(3),
    );
  }, [activeBoards, leaderboardData, windowSize]);

  const {
    autoScroll,
  } = leaderboardSettings;

  const dispatch = useDispatch();

  // React Spring animations for loading more, and auto scrolling.
  // Scrolling animation works by setting the scroll location from the tableScroll state
  const loadTransition = useSpring({
    position: 'absolute',
    opacity: isLeaderboardLoading ? 1 : 0,
  });

  const endTransition = useSpring({
    position: 'absolute',
    opacity: isEndOfList ? 1 : 0,
  });

  const autoScrollTransition = useSpring({
    scroll: tableScroll,
    from: { scroll: 0 },
  });

  // Auto Scroll functionality using custom useInterval hook.
  // The interval sets the next scroll location by adding the previous
  // scroll location (tableScroll) to the new one
  // (I.E. half of table container height) every 5 seconds.
  // The spring animation automatically goes to the updated scroll state.
  useInterval(() => {
    setTableScroll(tableScroll + (tableContainerHeight / 2));
  }, !autoScroll ? null : 5000);

  // Track the container's actual rendered height (not just on mount — auto-scroll
  // increments and the infinite-scroll trigger threshold both depend on this staying
  // accurate as the container resizes). Matches the ResizeObserver pattern in Board.js.
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setTableContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // INFINITE SCROLL VARIABLES:

  // Margins in the page
  const tableMargins = 30;

  // Initial Scroll location using react refs.
  const initialScrollLocation = tableRef.current ? tableRef.current.offsetTop : 0;

  // tableEnd is the location of the end of the list by subtracting the initial scroll location
  // from the table list height, then adding the container height and subtracting both margins.
  const tableEnd = (initialScrollLocation - tableHeight)
    + tableContainerHeight - (tableMargins * 2) + 10;

  // Load More function which fetches the next leaderboard page if it's not the end of the list.
  const loadMore = () => {
    if (!isEndOfList) {
      dispatch(fetchLeaderboard(leaderboardForm, 'loadMore', id));
    }
  };

  // Contd. Auto Scroll
  // If the board is at the end of the list, move the auto scroll location to 0 or the top.
  useEffect(() => {
    if (autoScroll === true && isEndOfList && (currentScroll <= tableEnd)) {
      setTimeout(() => {
        setTableScroll(0);
        setCurrentScroll(0);
      }, 1500);
    }
  }, [tableScroll]);

  return (
    <animated.div
      ref={tableContainerRef}
      scrollTop={autoScrollTransition.scroll}
      style={BoardTableStyles}
    >
      <LeaderboardTable
        activeBoards={activeBoards}
        handleLoadMore={loadMore}
        leaderboardData={data}
        leaderboardForm={leaderboardForm}
        tableEnd={tableEnd}
        tableRef={tableRef}
        setTableHeight={setTableHeight}
        setCurrentScroll={setCurrentScroll}
        windowSize={windowSize}
      />
      <LoadMoreContainer>
        <animated.div style={loadTransition}>
          <Spinner
            saving
            darkTheme
            large={false}
          />
        </animated.div>
        <animated.div style={endTransition}>
          <SubText>
            End of Leaderboard
          </SubText>
        </animated.div>
      </LoadMoreContainer>
    </animated.div>
  );
};

LeaderboardTableWrapper.propTypes = {
  activeBoards: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  windowSize: PropTypes.instanceOf(Object).isRequired,
};

export default LeaderboardTableWrapper;
